<script setup lang="ts">
import { CodeExtension, CodeNode } from "@lexical/code";
import { useElementSize } from "@vueuse/core";
import { defineExtension, type Klass, type LexicalEditor, type LexicalNode, type SerializedEditorState } from "lexical";
import { ContentEditable, HistoryPlugin, LexicalExtensionComposer, RichTextPlugin } from "lexical-vue";
import { computed, ref, watch } from "vue";

import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import ScrollbarOverlay from "~/components/dumb/ScrollbarOverlay.vue";
import { BACKUP_FONTS, colorsByTheme } from "~/constants/style";
import { usePageStore } from "~/stores";
import { isLexicalStateEmpty, someInHierarchy } from "~/utils/common";
import { printOrCaptureException } from "~/utils/exceptionClient";
import { when } from "~/utils/wait";

import { LEXICAL_OUTSIDE_CLASS } from "./const";
import CollaborationPlugin from "./plugins/CollaborationPlugin.vue";
import EditablePlugin from "./plugins/EditablePlugin.vue";
import EmojiMenuPlugin from "./plugins/EmojiMenuPlugin.vue";
import FocusPlugin from "./plugins/FocusPlugin.vue";
import HeadingNormalizationPlugin from "./plugins/HeadingNormalizationPlugin.vue";
import HorizontalRulePlugin from "./plugins/HorizontalRulePlugin.vue";
import ListNormalizationPlugin from "./plugins/ListNormalizationPlugin.vue";
import SerializedContentPlugin from "./plugins/SerializedContentPlugin.vue";
import SetEditorStatePlugin from "./plugins/SetEditorStatePlugin.vue";
import TextContentPlugin from "./plugins/TextContentPlugin.vue";
import ToggleSectionPlugin from "./plugins/ToggleSectionPlugin.vue";
import EDITOR_THEME from "./theme";

const props = defineProps<{
  namespace: string;
  nodes?: Klass<LexicalNode>[];
  initialState?: SerializedEditorState;
  placeholder: string;
  opacityBasedPlaceholderColor?: boolean;
  showPlaceholderOnHover?: boolean;
  disabled?: boolean;
  singleLine?: boolean;
  grow?: boolean;
  minHeightPx?: number;
  maxHeightPx?: number;
  topMarginPx?: number;
  bottomMarginPx?: number;
  leftMarginPx?: number;
  rightMarginPx?: number;
  unrounded?: boolean;
  extraRounded?: boolean;
  normallyBorderless?: boolean;
  alwaysBorderless?: boolean;
  highContrastBorder?: boolean;
  baseText?: boolean;
  largeText?: boolean;
  smallLeading?: boolean;
  noWrap?: boolean;
  editorClasses?: string;
  contentClasses?: string;
  collaboration?: {
    namespace: string;
    id: string;
  };
  overlayScrollbar?: boolean;
  dontFocusOnEditable?: boolean;
  isDraft?: boolean;
  dataTestid?: string;
  inlineButtons?: boolean;
}>();

const emit = defineEmits<{
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
}>();

const pageStore = usePageStore();

const colors = computed(() => colorsByTheme[pageStore.theme]);

const setEditorStatePlugin = ref<InstanceType<typeof SetEditorStatePlugin> | null>(null);
const focusManager = ref<InstanceType<typeof FocusPlugin> | null>(null);
const textContentPlugin = ref<InstanceType<typeof TextContentPlugin> | null>(null);
const serializedContentPlugin = ref<InstanceType<typeof SerializedContentPlugin> | null>(null);
const hasFocus = ref(false);

const isEditable = computed(() => !props.disabled);
const initialStateEmpty = !props.initialState || isLexicalStateEmpty(props.initialState);

const initializeEditorState = (editor: LexicalEditor) => {
  if (!props.initialState) {
    return;
  }

  const newState = editor.parseEditorState(props.initialState);
  editor.setEditorState(newState);
};

const editorExtension = defineExtension({
  name: "@dart/editor",
  dependencies: props.nodes?.includes(CodeNode) ? [CodeExtension] : [],
  namespace: props.namespace,
  theme: EDITOR_THEME,
  nodes: props.nodes ?? [],
  editable: isEditable.value,
  onError: (error: Error) => {
    printOrCaptureException(error);
  },
  $initialEditorState: props.collaboration
    ? null
    : props.initialState?.root.children.length
      ? initializeEditorState
      : undefined,
});

const topMarginPxNorm = computed(() => props.topMarginPx ?? 0);
const bottomMarginPxNorm = computed(() => `${props.bottomMarginPx ?? 0}px`);
const placeholderTopMarginPxNorm = computed(
  () =>
    `${topMarginPxNorm.value + (props.topMarginPx === 2 ? 0 : props.topMarginPx === 6 ? -4 : props.topMarginPx === -1 ? 3 : 2) + (props.largeText ? -2 : 2)}px`
);
const contentTopMarginPxNorm = computed(() => `${topMarginPxNorm.value - 2}px`);
const leftMarginPxNorm = computed(() => `${(props.leftMarginPx ?? 6) - 2}px`);

const editorRef = ref<HTMLElement | null>(null);

const synced = ref(!props.collaboration);
const syncKey = ref(0);

const rendered = ref(false);
const { height } = useElementSize(editorRef);
const { stop } = watch(height, () => {
  if (height.value === 0) {
    return;
  }
  rendered.value = true;
  stop();
});

const buttonsRef = ref<HTMLElement | null>(null);
const singleLineHeight = ref<number | null>(null);

const { width: buttonsWidth } = useElementSize(buttonsRef);
const { stop: stopCapture } = watch(height, () => {
  if (!props.inlineButtons || height.value === 0) {
    return;
  }
  singleLineHeight.value = height.value;
  stopCapture();
});
const buttonsInline = computed(
  () => props.inlineButtons !== undefined && (props.inlineButtons || height.value <= (singleLineHeight.value ?? 0))
);
const rightMarginPxNorm = computed(() => {
  const extra = props.inlineButtons !== undefined && buttonsInline.value ? Math.ceil(buttonsWidth.value) : 0;
  return `${(props.rightMarginPx ?? 6) - 2 + extra}px`;
});

const isReady = computed(() => synced.value && rendered.value);

const collaborationPlugin = ref<InstanceType<typeof CollaborationPlugin> | null>(null);
const collaborationProvider = computed(() => collaborationPlugin.value?.provider ?? null);
let hasSynced = false;
const shouldShowConnectionSpinner = ref(!!props.collaboration);
const onCollaborationReady = () => {
  synced.value = true;
  hasSynced = true;
  shouldShowConnectionSpinner.value = false;
};
const onCollaborationDisconnect = () => {
  synced.value = false;
  if (hasSynced) {
    syncKey.value += 1;
  }
};

const focusInner = (start?: boolean) => focusManager.value?.focus(start);
const focusEndInner = () => focusManager.value?.focusEnd();
const focusNearestBlockInner = (clientX: number, clientY: number) =>
  focusManager.value?.focusNearestBlock(clientX, clientY);

const focus = (start?: boolean) => {
  if (synced.value) {
    focusInner(start);
    return;
  }
  when(synced, () => focusInner(start));
};

const focusEnd = () => {
  if (synced.value) {
    focusEndInner();
    return;
  }
  when(synced, focusEndInner);
};

const focusNearestBlock = (clientX: number, clientY: number) => {
  if (synced.value) {
    focusNearestBlockInner(clientX, clientY);
    return;
  }
  when(synced, () => focusNearestBlockInner(clientX, clientY));
};

const onFocus = (event: FocusEvent) => {
  hasFocus.value = true;
  emit("focus", event);
};

const onBlur = (event: FocusEvent) => {
  if (
    someInHierarchy(
      event.relatedTarget as HTMLElement | null,
      (e) => e === editorRef.value || e.classList.contains(LEXICAL_OUTSIDE_CLASS)
    )
  ) {
    return;
  }

  hasFocus.value = false;
  emit("blur", event);
};

const setEditorState = (state: SerializedEditorState) => setEditorStatePlugin.value?.setEditorState(state);

const getTextContent = () => textContentPlugin.value?.getTextContent();

const getSerializedNodes = () => serializedContentPlugin.value?.getSerializedNodes() ?? [];

const updateFromMarkdown = (markdown: string, showAiCursor = false) =>
  serializedContentPlugin.value?.updateFromMarkdown(markdown, showAiCursor);

defineExpose({
  editorElem: editorRef,
  focus,
  focusEnd,
  focusNearestBlock,
  hasFocus,
  isReady,
  buttonsInline,
  getTextContent,
  getSerializedNodes,
  updateFromMarkdown,
  setEditorState,
  collaborationProvider,
});
</script>

<template>
  <TextEditor v-if="!synced" v-bind="{ ...props, disabled: true, collaboration: undefined }">
    <template #before>
      <slot name="before" />
    </template>
    <slot />
    <template #after>
      <slot name="after" />
    </template>
    <LoadingSpinner v-if="shouldShowConnectionSpinner" class="absolute top-1.5 right-[7px] !w-fit" x-small immediate />
  </TextEditor>
  <LexicalExtensionComposer :key="syncKey" :extension="editorExtension">
    <div
      class="group/text-editor relative isolate"
     
      :class="[
        editorClasses,
        {
          border: !alwaysBorderless,
          'hover:border-md': !alwaysBorderless && !highContrastBorder && !hasFocus,
          'hover:border-hvy': !alwaysBorderless && highContrastBorder && !hasFocus,
          'border-transparent': !alwaysBorderless && normallyBorderless && !highContrastBorder && !hasFocus,
          'border-lt': !alwaysBorderless && !normallyBorderless && !highContrastBorder && !hasFocus,
          'border-md': !alwaysBorderless && highContrastBorder !== hasFocus,
          'border-primary-base': !alwaysBorderless && highContrastBorder && hasFocus,
          rounded: !unrounded,
          'rounded-lg': extraRounded,
          'w-full': !grow,
          'w-auto': grow,
          'min-w-[40px]': grow && isEditable,
          hidden: !synced,
        },
      ]">
      <div class="contents" @click="() => focus()" @keydown.enter.prevent="() => focus()">
        <slot name="before" />
      </div>
      <div
        ref="editorRef"
        class="dart-editor relative outline-none"
        tabindex="-1"
        :class="collaboration ? 'dart-cursors-container' : undefined"
        :style="{ '--textColor': colors.textMd }">
        <EditablePlugin :editable="isEditable" :dont-focus="dontFocusOnEditable" />
        <FocusPlugin v-if="isEditable" ref="focusManager" :has-focus="hasFocus" />
        <RichTextPlugin>
          <template v-if="placeholder && (isEditable || initialStateEmpty)" #placeholder>
            <div
              class="pointer-events-none absolute top-0 pl-0.5 select-none"
              :class="{
                'text-2xl font-semibold': largeText,
                'font-normal': !largeText,
                'text-gray-900/30 dark:text-white/20': opacityBasedPlaceholderColor,
                'text-faint': !opacityBasedPlaceholderColor,
                'text-sm/6': !baseText && !largeText && !smallLeading,
                'text-base': baseText && !largeText && !smallLeading,
                'text-sm/5': !largeText && smallLeading,
                '!whitespace-nowrap': noWrap && !singleLine,
                'opacity-0 transition-opacity group-focus-within/text-editor:opacity-100 group-hover/text-editor:opacity-100':
                  showPlaceholderOnHover,
              }"
              :style="{
                marginTop: placeholderTopMarginPxNorm,
                marginLeft: leftMarginPxNorm,
                marginRight: rightMarginPxNorm,
              }">
              {{ placeholder }}
            </div>
          </template>
          <template #contentEditable>
            <ScrollbarOverlay
              v-if="overlayScrollbar && maxHeightPx !== undefined"
              :style="{ maxHeight: `${maxHeightPx}px`, marginTop: contentTopMarginPxNorm }">
              <ContentEditable
                v-bind="{ 'data-testid': 'text-editor', ariaAutoComplete: 'both' }"
                autocapitalize="on"
                spellcheck
                autocorrect="on"
                class="relative rounded-sm px-0.5 text-sm/6 font-normal text-md focus-ring-none"
                :style="{
                  minHeight: minHeightPx !== undefined ? `${minHeightPx}px` : undefined,
                  marginLeft: leftMarginPxNorm,
                  marginRight: rightMarginPxNorm,
                  paddingBottom: bottomMarginPxNorm,
                }"
                @focus="onFocus"
                @blur="onBlur"
                @keydown.esc.stop.prevent />
            </ScrollbarOverlay>
            <ContentEditable
              v-else
              v-bind="{ 'data-testid': 'text-editor', ariaAutoComplete: 'both' }"
              autocapitalize="on"
              spellcheck
              autocorrect="on"
              :class="[
                contentClasses,
                {
                  rounded: !unrounded,
                  'overflow-hidden !whitespace-pre': singleLine,
                  'text-2xl font-semibold': largeText,
                  'font-normal': !largeText,
                  'text-sm/6': !baseText && !largeText && !smallLeading,
                  'text-base': baseText && !largeText && !smallLeading,
                  'text-sm/5': !largeText && smallLeading,
                  '!whitespace-nowrap': noWrap && !singleLine,
                  'overflow-y-auto': maxHeightPx !== undefined,
                },
              ]"
              class="relative px-0.5 text-md focus-ring-none"
              :style="{
                minHeight: minHeightPx !== undefined ? `${minHeightPx}px` : undefined,
                maxHeight: maxHeightPx !== undefined ? `${maxHeightPx}px` : undefined,
                marginTop: contentTopMarginPxNorm,
                marginLeft: leftMarginPxNorm,
                marginRight: rightMarginPxNorm,
                paddingBottom: bottomMarginPxNorm,
              }"
              @focus="onFocus"
              @blur="onBlur"
              @keydown.esc.stop.prevent />
          </template>
        </RichTextPlugin>
        <slot />
        <CollaborationPlugin
          v-if="collaboration"
          :id="collaboration.id"
          ref="collaborationPlugin"
          :namespace="collaboration.namespace"
          :cursors-container-ref="editorRef"
          @ready="onCollaborationReady"
          @disconnect="onCollaborationDisconnect"
          @connection-error="shouldShowConnectionSpinner = true" />
        <SetEditorStatePlugin ref="setEditorStatePlugin" />
        <!-- Custom plugins -->
        <EmojiMenuPlugin v-if="isEditable && !pageStore.isMobile" />
        <HeadingNormalizationPlugin />
        <HorizontalRulePlugin />
        <HistoryPlugin v-if="!collaboration" />
        <ListNormalizationPlugin />
        <TextContentPlugin ref="textContentPlugin" />
        <SerializedContentPlugin ref="serializedContentPlugin" />
        <ToggleSectionPlugin />
        <div
          ref="buttonsRef"
          :class="buttonsInline ? 'absolute inset-y-0 right-0 flex items-center' : 'contents'"
          @click="() => focus()"
          @keydown.enter.prevent="() => focus()">
          <slot name="after" />
        </div>
      </div>
    </div>
  </LexicalExtensionComposer>
</template>

<style>
@reference "#frontend.css";
.dart-cursors-container > div:last-child > span > span:last-child > span:first-child {
  border-radius: 2px;
  top: unset !important;
  bottom: -16px !important;
  font-family: "Inter var", v-bind("BACKUP_FONTS") !important;
  color: var(--textColor) !important;
  padding: 2px 4px !important;
}

::selection {
  @apply !bg-primary-hover-dark/30 dark:!bg-primary-hover-dark/30;
}
</style>
